/* eslint-disable camelcase */
import React, { memo, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { mdiDelete, mdiImagePlus, mdiMagnifyPlusOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { useSelector } from 'react-redux'

import BaseFilterModal from '../../../../components/hooks/BaseFilterModal'
import filterStylesComponents from '../../../../assets/filterComponentsStyles'
import colors from '../../../../assets/colors'

const ACCEPTED_MIMES = 'image/png,image/jpeg'

/**
 * @author Heitor Santos 30/07/2026
 * Fotos do item de serviço. Trabalha sobre `item.images`, um array único que
 * mistura fotos já gravadas (`kind: 'path'`) e uploads pendentes (`kind: 'file'`),
 * na ordem em que aparecem aqui. A sincronização acontece depois do save do
 * atendimento, em `syncServiceImages`.
 */
function ItemImages({ open, item, handleClose, onSave, disabled = false }) {
  const classes = filterStylesComponents()

  const documentContexts = useSelector(
    state => state.attendance.documentContexts
  )

  const [images, setImages] = useState([])
  // Foto ampliada dentro do próprio modal (nada de abrir em outra aba).
  const [zoomed, setZoomed] = useState(null)

  const inputRef = useRef(null)
  const uidRef = useRef(0)
  // Previews criados nesta sessão do modal. Só eles podem ser revogados no
  // cancelar/remover — os demais pertencem ao estado já salvo no form.
  const ownedPreviews = useRef([])

  const revokeOwnedPreviews = () => {
    ownedPreviews.current.forEach(preview => URL.revokeObjectURL(preview))
    ownedPreviews.current = []
  }

  useEffect(() => {
    if (!open) return

    setImages((item?.images || []).map(image => ({ ...image })))
    setZoomed(null)
    ownedPreviews.current = []
  }, [open, item?.images])

  useEffect(() => revokeOwnedPreviews, [])

  const handleCancel = () => {
    revokeOwnedPreviews()
    handleClose()
  }

  const handleAddFiles = event => {
    const files = Array.from(event.target.files || [])

    const newImages = files.map(file => {
      const preview = URL.createObjectURL(file)
      ownedPreviews.current.push(preview)

      uidRef.current += 1

      return {
        uid: `f-${uidRef.current}-${file.name}`,
        kind: 'file',
        context: '',
        file,
        preview,
      }
    })

    setImages(prev => [...prev, ...newImages])

    // Permite escolher o mesmo arquivo novamente depois de removê-lo.
    event.target.value = ''
  }

  const handleRemove = uid => {
    setImages(prev => {
      const target = prev.find(image => image.uid === uid)

      if (target?.preview && ownedPreviews.current.includes(target.preview)) {
        URL.revokeObjectURL(target.preview)
        ownedPreviews.current = ownedPreviews.current.filter(
          preview => preview !== target.preview
        )
      }

      return prev.filter(image => image.uid !== uid)
    })
  }

  const handleChangeContext = (uid, context) => {
    setImages(prev =>
      prev.map(image => (image.uid === uid ? { ...image, context } : image))
    )
  }

  // Contexto é obrigatório na foto nova. Na foto já gravada é opcional — omitido,
  // o backend preserva o contexto atual (fotos antigas podem vir sem nenhum).
  const pendingContext = images.some(
    image => image.kind === 'file' && !image.context
  )

  const handleSubmit = event => {
    event.preventDefault()

    if (pendingContext) return

    // As previews passam a pertencer ao form, não podem ser revogadas no fechar.
    ownedPreviews.current = []

    onSave(images)
    handleClose()
  }

  return (
    <BaseFilterModal
      open={open}
      title="Fotos do Item"
      hiddenHelpButton
      handleOpen={handleCancel}
      paperWidth="600px"
    >
      <Grid item xs={12}>
        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              container
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Typography
                  style={{ fontSize: '14px', color: colors.textGray }}
                >
                  {item?.name}
                </Typography>
              </Grid>

              {!disabled && (
                <Grid item>
                  <Button
                    type="button"
                    className={classes.btnAdd}
                    onClick={() => inputRef.current?.click()}
                  >
                    <Icon
                      path={mdiImagePlus}
                      size={0.8}
                      style={{ marginRight: '8px' }}
                    />
                    Adicionar fotos
                  </Button>

                  <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_MIMES}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleAddFiles}
                  />
                </Grid>
              )}
            </Grid>

            {!documentContexts?.length && (
              <Grid item xs={12}>
                <Typography style={{ fontSize: '13px', color: colors.error }}>
                  Não foi possível carregar os contextos das fotos. Recarregue a
                  página para tentar novamente.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              {!images.length && (
                <Typography
                  style={{
                    fontSize: '14px',
                    color: colors.textPrimary,
                    padding: '24px 0',
                    textAlign: 'center',
                  }}
                >
                  Nenhuma foto neste item.
                </Typography>
              )}

              {images.length > 0 && (
                <Box
                  style={{
                    maxHeight: '420px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }}
                >
                  {images.map(image => (
                    /**
                     * Linha em flex com `gap` em vez de `Grid spacing`: as margens
                     * negativas do Grid estouram a largura do container com
                     * `overflow` e criam barras de scroll desnecessárias.
                     */
                    <Box
                      key={image.uid}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '16px',
                        padding: '8px 0',
                        borderBottom: `1px dashed ${colors.graylight}`,
                      }}
                    >
                      <Box>
                        <Tooltip title="Clique para ampliar">
                          <ButtonBase
                            onClick={() => setZoomed(image)}
                            style={{
                              borderRadius: '8px',
                              position: 'relative',
                              display: 'block',
                            }}
                          >
                            <img
                              src={image.link || image.preview}
                              alt={image.path || image.file?.name}
                              style={{
                                width: '180px',
                                height: '180px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: `1px solid ${colors.graylight}`,
                                display: 'block',
                              }}
                            />

                            <Box
                              style={{
                                position: 'absolute',
                                right: '4px',
                                bottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                borderRadius: '50%',
                                padding: '3px',
                              }}
                            >
                              <Icon
                                path={mdiMagnifyPlusOutline}
                                size={0.8}
                                color="#FFFF"
                              />
                            </Box>
                          </ButtonBase>
                        </Tooltip>
                      </Box>

                      <Box>
                        <TextField
                          select
                          label="Contexto"
                          style={{ width: '220px' }}
                          value={image.context || ''}
                          disabled={disabled}
                          error={image.kind === 'file' && !image.context}
                          helperText={
                            image.kind === 'file' && !image.context
                              ? 'Selecione o contexto'
                              : ''
                          }
                          onChange={event =>
                            handleChangeContext(image.uid, event.target.value)
                          }
                        >
                          {(documentContexts || []).map(documentContext => (
                            <MenuItem
                              key={documentContext.id}
                              value={documentContext.context}
                            >
                              {documentContext.context}
                            </MenuItem>
                          ))}
                        </TextField>

                        {image.kind === 'file' && (
                          <Typography
                            style={{
                              fontSize: '12px',
                              color: colors.textPrimary,
                            }}
                          >
                            Nova foto - {image.file?.name}
                          </Typography>
                        )}
                      </Box>

                      {!disabled && (
                        <Box>
                          <Tooltip title="Remover foto">
                            <IconButton
                              type="button"
                              onClick={() => handleRemove(image.uid)}
                            >
                              <Icon
                                path={mdiDelete}
                                size={1}
                                color={colors.error}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              container
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                type="button"
                onClick={handleCancel}
                className={classes.btnMoreFilters}
              >
                Cancelar
              </Button>

              <Tooltip
                title={
                  pendingContext
                    ? 'Defina o contexto de todas as fotos para salvar'
                    : ''
                }
              >
                <span>
                  <Button
                    type="submit"
                    className={classes.btnAdd}
                    disabled={disabled || pendingContext}
                  >
                    Salvar
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </form>
      </Grid>

      <Modal
        open={Boolean(zoomed)}
        onClose={() => setZoomed(null)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          onClick={() => setZoomed(null)}
          style={{ outline: 'none', cursor: 'zoom-out', lineHeight: 0 }}
        >
          <img
            src={zoomed?.link || zoomed?.preview}
            alt={zoomed?.path || zoomed?.file?.name}
            style={{
              maxWidth: '88vw',
              maxHeight: '86vh',
              objectFit: 'contain',
              borderRadius: '8px',
              backgroundColor: '#FFFF',
            }}
          />
        </Box>
      </Modal>
    </BaseFilterModal>
  )
}

export default memo(ItemImages)
